import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from '@/types/carAuctionClient';
import {
  clearNativeTokens,
  getNativeHashPath,
  getNativeTokens,
  isNativeCapacitorApp,
  saveNativeTokens,
  type NativeAuthTokens,
} from './nativeAuth';

// API base configuration
// Configure via .env file with VITE_API_BASE_URL
// Example for local development (.env or .env.local):
//   VITE_API_BASE_URL=http://localhost:5000/api/v1
// Example for production (.env.production):
//   VITE_API_BASE_URL=https://backendserver.ondigitalocean.app/api/v1
// Falls back to localhost if VITE_API_BASE_URL is not set
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api/v1';

if (
  import.meta.env.VITE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  isNativeCapacitorApp()
) {
  console.info('[Bilenia] Native API base URL:', API_BASE_URL);
}

const AUTH_SKIP_REFRESH_URLS = ['/auth/refresh', '/auth/login', '/auth/logout'];

const PUBLIC_APP_PATHS = [
  '/auth',
  '/car-valuation-embed',
  '/listings-embed',
  '/',
  '/hur-det-fungerar',
  '/salj-bilen',
  '/kop-bil',
  '/how-it-works',
  '/sell-car',
  '/buy-car',
  '/verify-email',
  '/accept-terms',
  '/reset-password',
  '/mask-data',
  '/privacy',
  '/contact',
];

let isRefreshing = false;
let sessionInvalid = false;
let authRedirectDone = false;
/** After failed refresh on web, stop hammering /auth/refresh until next login. */
let refreshBlocked = false;
/** While AuthContext cold-starts, never clear native tokens from interceptor races. */
let nativeAuthBootstrapping = false;

export function beginNativeAuthBootstrap(): void {
  if (isNativeCapacitorApp()) {
    nativeAuthBootstrapping = true;
  }
}

export function endNativeAuthBootstrap(): void {
  nativeAuthBootstrapping = false;
}

let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: unknown = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

function normalizePathname(pathname: string): string {
  const p = pathname.replace(/\/+$/, '');
  return p || '/';
}

function isPublicAppPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return PUBLIC_APP_PATHS.some(
    (route) => path === route || path.startsWith(`${route}/`)
  );
}

function isAuthRefreshSkippableUrl(url?: string): boolean {
  if (!url) return false;
  return AUTH_SKIP_REFRESH_URLS.some((path) => url.includes(path));
}

function isPasswordUpdateEndpoint(url?: string): boolean {
  if (!url) return false;
  return (
    url.includes('/users/updatePassword') ||
    url.includes('/admin/profile/password')
  );
}

/** Profile probes may 401 without meaning the whole session is dead (wrong endpoint for role). */
function isSessionProbeUrl(url?: string): boolean {
  if (!url) return false;
  return url.includes('/users/me') || url.includes('/admin/profile');
}

function shouldRedirectToAuth(redirectTarget: string): boolean {
  const pathname = redirectTarget.split('?')[0] || '/';
  if (isPublicAppPath(pathname)) return false;
  if (normalizePathname(pathname) === '/auth') return false;
  return true;
}

function getCurrentAppPath(): string {
  if (typeof window === 'undefined') return '/';
  if (isNativeCapacitorApp()) return getNativeHashPath();
  return window.location.pathname;
}

function getNativeRedirectTarget(): string {
  if (typeof window === "undefined") return "/dashboard";
  if (isNativeCapacitorApp()) return getNativeHashPath() || "/dashboard";
  const hash = window.location.hash.replace(/^#/, "");
  return hash || "/dashboard";
}

function redirectToAuth(redirectTarget: string) {
  if (!isNativeCapacitorApp()) return;
  if (!shouldRedirectToAuth(redirectTarget)) return;
  if (authRedirectDone) return;
  authRedirectDone = true;

  const url = `/#/auth?redirect=${encodeURIComponent(redirectTarget)}`;
  window.location.replace(url);
}

function markSessionInvalid(redirectTarget?: string) {
  if (isNativeCapacitorApp()) {
    if (nativeAuthBootstrapping) return;
    sessionInvalid = true;
    clearNativeTokens();
    if (redirectTarget) redirectToAuth(redirectTarget);
  }
}

function applyNativeAuthHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (!isNativeCapacitorApp()) return config;

  config.headers.set('X-Bilenia-Client', 'native');

  const tokens = getNativeTokens();
  if (tokens?.accessToken) {
    config.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }
  return config;
}

function resetAuthRetryState() {
  refreshBlocked = false;
  sessionInvalid = false;
  authRedirectDone = false;
}

export function resetNativeAuthClientState() {
  resetAuthRetryState();
}

/** Web alias — reset after login so a prior failed refresh does not block new session. */
export const resetAuthRefreshState = resetNativeAuthClientState;

export function persistNativeTokensFromPayload(data: unknown) {
  if (!data || typeof data !== 'object') return;
  const tokens = (data as { nativeTokens?: NativeAuthTokens }).nativeTokens;
  if (tokens?.accessToken && tokens?.refreshToken) {
    saveNativeTokens(tokens);
    resetAuthRetryState();
  }
}

function storeNativeTokensFromResponse(data: unknown) {
  persistNativeTokensFromPayload(data);
}

export type NativeRefreshResult = 'ok' | 'unauthorized' | 'failed';

let nativeSessionRefreshPromise: Promise<NativeRefreshResult> | null = null;

function getErrorStatus(error: unknown): number | undefined {
  const e = error as { status?: number; response?: { status?: number } };
  return e?.status ?? e?.response?.status;
}

/** Single-flight native refresh (boot, resume, interceptor). */
export async function refreshNativeSession(): Promise<NativeRefreshResult> {
  if (!isNativeCapacitorApp()) return 'failed';

  if (nativeSessionRefreshPromise) {
    return nativeSessionRefreshPromise;
  }

  nativeSessionRefreshPromise = (async () => {
    const tokens = getNativeTokens();
    if (!tokens?.refreshToken) return 'failed';

    try {
      const response = await apiClient.post('/auth/refresh', {
        refreshToken: tokens.refreshToken,
      });
      const payload = response.data as {
        nativeTokens?: NativeAuthTokens;
      };
      const nativeTokens = payload?.nativeTokens;
      if (!nativeTokens?.accessToken || !nativeTokens?.refreshToken) {
        if (import.meta.env.DEV) {
          console.warn(
            '[Bilenia auth] refresh succeeded but nativeTokens missing in response',
          );
        }
        return 'failed';
      }
      saveNativeTokens(nativeTokens);
      resetAuthRetryState();
      return 'ok';
    } catch (error) {
      const status = getErrorStatus(error);
      if (status === 401 || status === 403) {
        return 'unauthorized';
      }
      return 'failed';
    } finally {
      nativeSessionRefreshPromise = null;
    }
  })();

  return nativeSessionRefreshPromise;
}

export function invalidateNativeSession(redirectTarget?: string) {
  markSessionInvalid(redirectTarget);
}

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: isNativeCapacitorApp() ? 30000 : 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

apiClient.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    if (isAuthRefreshSkippableUrl(config.url)) {
      config.headers.delete('Authorization');
    }
    return applyNativeAuthHeaders(config);
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    const url = response.config.url ?? '';
    if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
      resetAuthRetryState();
    }
    storeNativeTokensFromResponse(response.data);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status as number | undefined;
      const currentPath = getCurrentAppPath();
      const publicRoutes = isNativeCapacitorApp()
        ? ['/auth', '/auctions']
        : ['/auth', '/car-valuation-embed', '/listings-embed', '/', '/hur-det-fungerar', '/salj-bilen', '/kop-bil', '/how-it-works', '/sell-car', '/buy-car', '/verify-email', '/accept-terms', '/reset-password', '/mask-data', '/privacy', '/contact', '/auctions'];
      const hasNativeSession =
        isNativeCapacitorApp() && Boolean(getNativeTokens()?.refreshToken);
      const isPublicRoute =
        !hasNativeSession &&
        publicRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'));

    const code = error.response?.data?.code as string | undefined;
    const requestUrl = originalRequest?.url as string | undefined;

      // Guest on public pages: don't refresh. Logged-in users on /auctions still send
      // expired cookies — when the server asks for refresh, always attempt it.
      if (isPublicRoute && code !== 'REFRESH_ACCESS_TOKEN_NECESSARY') {
        const apiError: ApiError = {
          message: error.response?.data?.error || error.response?.data?.message || error.message || 'Unauthorized',
          errorCode: error.response?.data?.errorCode || error.response?.data?.code || null,
          status: error.response?.status || 401,
          details: error.response?.data || error,
        };
        return Promise.reject(apiError);
      }

    if (sessionInvalid && !isAuthRefreshSkippableUrl(requestUrl)) {
      const apiError: ApiError = {
        message: error.response?.data?.error || error.message || 'Unauthorized',
        errorCode: error.response?.data?.errorCode || error.response?.data?.code || null,
        status: status || 401,
        details: error.response?.data || error,
      };
      return Promise.reject(apiError);
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      if (isAuthRefreshSkippableUrl(requestUrl)) {
        if (!isNativeCapacitorApp()) {
          refreshBlocked = true;
        }
        const apiError: ApiError = {
          message: error.response?.data?.error || error.message || 'Unauthorized',
          errorCode: code || null,
          status: 401,
          details: error.response?.data || error,
        };
        return Promise.reject(apiError);
      }

      // Wrong endpoint (e.g. dealer hitting /admin/profile) — not a hard logout.
      if (code === 'ADMIN_NOT_FOUND') {
        const apiError: ApiError = {
          message: error.response?.data?.error || error.message || 'Unauthorized',
          errorCode: code || null,
          status: 401,
          details: error.response?.data || error,
        };
        return Promise.reject(apiError);
      }

      // Wrong current password etc. — not a session expiry; surface API error to the form.
      if (isPasswordUpdateEndpoint(requestUrl)) {
        const apiError: ApiError = {
          message: error.response?.data?.error || error.message || 'Unauthorized',
          errorCode: code || null,
          status: 401,
          details: error.response?.data || error,
        };
        return Promise.reject(apiError);
      }

      // AuthContext owns refresh during cold start — avoid double-rotation races.
      if (nativeAuthBootstrapping) {
        const apiError: ApiError = {
          message: error.response?.data?.error || error.message || 'Unauthorized',
          errorCode: code || null,
          status: 401,
          details: error.response?.data || error,
        };
        return Promise.reject(apiError);
      }

      if (
        code === 'REFRESH_ACCESS_TOKEN_NECESSARY' &&
        refreshBlocked &&
        !isNativeCapacitorApp()
      ) {
        const apiError: ApiError = {
          message: error.response?.data?.error || error.message || 'Unauthorized',
          errorCode: code || null,
          status: 401,
          details: error.response?.data || error,
        };
        return Promise.reject(apiError);
      }

      if (
        code !== 'REFRESH_ACCESS_TOKEN_NECESSARY' &&
        !(isNativeCapacitorApp() && Boolean(getNativeTokens()?.refreshToken))
      ) {
        const currentPath = getCurrentAppPath();

        const skipNativeSessionClear = isSessionProbeUrl(requestUrl);

        if (
          typeof window !== 'undefined' &&
          !isPublicAppPath(currentPath) &&
          !isNativeCapacitorApp()
        ) {
          const redirectTarget = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          window.location.href = `/auth?redirect=${encodeURIComponent(redirectTarget)}`;
        } else if (
          typeof window !== 'undefined' &&
          !isPublicAppPath(currentPath) &&
          isNativeCapacitorApp() &&
          !skipNativeSessionClear
        ) {
          markSessionInvalid(getNativeRedirectTarget());
        }

        const apiError: ApiError = {
          message: error.response?.data?.error || error.message || 'Unauthorized',
          errorCode: error.response?.data?.errorCode || error.response?.data?.code || null,
          status: 401,
          details: error.response?.data || error,
        };
        return Promise.reject(apiError);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((queueError) => Promise.reject(queueError));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        if (isNativeCapacitorApp()) {
          const refreshed = await refreshNativeSession();
          if (refreshed === 'unauthorized') {
            const redirectTarget = getNativeRedirectTarget();
            if (shouldRedirectToAuth(redirectTarget)) {
              markSessionInvalid(redirectTarget);
            } else {
              markSessionInvalid();
            }
            throw error;
          }
          if (refreshed !== 'ok') {
            processQueue(error);
            const apiError: ApiError = {
              message:
                error.response?.data?.error ||
                error.message ||
                'Session refresh failed',
              errorCode: code || null,
              status: status || 401,
              details: error.response?.data || error,
            };
            return Promise.reject(apiError);
          }
        } else {
          const refreshResponse = await apiClient.post('/auth/refresh');
          storeNativeTokensFromResponse(refreshResponse.data);
        }
        processQueue();
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        // Only hard-block further refresh attempts when the refresh token itself
        // is rejected. Transient network/5xx failures should allow "Try again".
        if (!isNativeCapacitorApp()) {
          const refreshStatus = (refreshError as { response?: { status?: number } })
            ?.response?.status;
          if (refreshStatus === 401 || refreshStatus === 403) {
            refreshBlocked = true;
          }
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const apiError: ApiError = {
      message:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'An unexpected error occurred',
      errorCode: error.response?.data?.errorCode || error.response?.data?.code || null,
      status: status || 500,
      details: error.response?.data || error,
    };

    return Promise.reject(apiError);
  }
);

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((response) => response.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((response) => response.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((response) => response.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((response) => response.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((response) => response.data),
};

export default apiClient;
